import isEqual from 'lodash.isequal';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import logError from '@alltrails/core/utils/logError';
import { type Costings, CustomRouteActivityType } from '@alltrails/types/CustomRoute';
import { useAppDispatch } from '../../redux';
import { setToastMessage } from '../../redux/customRoute';
import { transformSegmentsToServerRoutes } from '../../redux/customRoutesApi';
import { CustomRoutePath, CustomRouteSegment, GroupedSegment, PathGroup } from '../../types/CustomRoute';
import { getGeneralError } from '../../utils/customRoutes/constants';
import { isGroupedSegmentRoutable, isSegmentRoutable } from '../../utils/customRoutes/validation';
import useRouting from './useRouting';

type RerouteSegmentsOptions = {
  segments: CustomRouteSegment[];
  newActivity?: CustomRouteActivityType;
  costings?: Costings;
};

export type RerouteSegmentsResult = { isSuccess: boolean; data?: { segments?: CustomRouteSegment[]; costings: Costings } };

/**
 * Combine the free-drawn paths from an array of segments with the rerouted non-free-drawn paths from an array of corresponding grouped segments.
 * @param currentSegments The segments for which rerouting was requested containing all paths, including free-drawn paths
 * @param newGroupedSegments The grouped segments returned from the server containing only groups of contiguous non-free-drawn paths
 * @returns The complete segments with the original free-drawn paths interleaved with the new paths from the server
 */
const updateSegments = (currentSegments: CustomRouteSegment[], newGroupedSegments: GroupedSegment[]): CustomRouteSegment[] | undefined => {
  // The number of segments must match between the routable current segments and the new segments
  if (currentSegments.filter(isSegmentRoutable).length !== newGroupedSegments.length) {
    logError(new Error('Segment count mismatch between current and new segments.'));
    return;
  }

  // Split the current segments into path groups of contiguous paths with the same freeDraw setting
  const currentGroupedSegments = currentSegments.map(segment => {
    const currentPathGroups = segment.paths.reduce<PathGroup[]>((acc, path) => {
      if (acc.length === 0) {
        acc.push({ paths: [path] });
        return acc;
      }

      const lastPathGroup = acc[acc.length - 1];
      const lastPath = lastPathGroup.paths[lastPathGroup.paths.length - 1];
      if (!!lastPath.freeDraw === !!path.freeDraw) {
        lastPathGroup.paths.push(path);
      } else {
        acc.push({ paths: [path] });
      }

      return acc;
    }, []);

    return {
      ...segment,
      pathGroups: currentPathGroups
    };
  });

  // The number of groups of contiguous non-free-drawn paths must match between the current segments with at least two points and the new segments
  if (
    currentGroupedSegments
      .filter(isGroupedSegmentRoutable)
      .some(
        (segment, index) =>
          segment.pathGroups.filter(pathGroup => !pathGroup.paths[0]?.freeDraw).length !== newGroupedSegments[index].pathGroups.length
      )
  ) {
    logError(new Error('Path group count mismatch between current and new segments.'));
    return;
  }

  let routableSegmentIndex = 0;
  return currentGroupedSegments.map((groupedSegment, index) => {
    if (!isGroupedSegmentRoutable(groupedSegment)) {
      // If the grouped segment has no paths with points, return it as is
      return currentSegments[index];
    }

    const { pathGroups: currentPathGroups, ...rest } = groupedSegment;
    const newPathGroups = newGroupedSegments[routableSegmentIndex].pathGroups;
    let newPathGroup = newPathGroups.shift();

    const paths = currentPathGroups.reduce<CustomRoutePath[]>((acc, currentPathGroup) => {
      if (currentPathGroup.paths[0].freeDraw) {
        // If the current path group is free-drawn, add all paths from the current segment
        acc.push(...currentPathGroup.paths);
      } else if (newPathGroup) {
        const newPaths = [...newPathGroup.paths];

        // If the first point of the new path group does not match the first point of the current path group, insert the current first point
        const newFirstPath = newPaths[0];
        const newFirstPoint = newFirstPath.points[0];
        const currentFirstPoint = currentPathGroup.paths[0].points[0];
        if (!isEqual(newFirstPoint, currentFirstPoint)) {
          newFirstPath.points.unshift(currentFirstPoint);
        }

        // If the last point of the new path group does not match the last point of the current path group, add the current last point
        const newLastPath = newPaths[newPaths.length - 1];
        const newLastPoint = newLastPath.points[newLastPath.points.length - 1];
        const currentLastPath = currentPathGroup.paths[currentPathGroup.paths.length - 1];
        const currentLastPoint = currentLastPath.points[currentLastPath.points.length - 1];
        if (!isEqual(newLastPoint, currentLastPoint)) {
          newLastPath.points.push(currentLastPoint);
        }

        // If the current path group is not free-drawn, add the paths from the new path group and retrieve the next new path group
        acc.push(...newPaths);

        newPathGroup = newPathGroups.shift();
      }

      return acc;
    }, []);

    routableSegmentIndex++;

    return {
      ...rest,
      paths
    };
  });
};

const useRerouteSegments = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const routing = useRouting();

  const rerouteSegments = useCallback(
    async ({ segments, newActivity, costings }: RerouteSegmentsOptions): Promise<RerouteSegmentsResult> => {
      if (segments.length === 0 || segments.every(segment => !isSegmentRoutable(segment))) {
        return { isSuccess: false };
      }

      const { isSuccess, data } = await routing({ routes: transformSegmentsToServerRoutes(segments), newActivity, costings });

      if (!isSuccess || !data) {
        return { isSuccess: false };
      }

      const { groupedSegments, ...rest } = data;
      const updatedSegments = updateSegments(segments, JSON.parse(JSON.stringify(groupedSegments)));

      if (!updatedSegments) {
        dispatch(setToastMessage(getGeneralError(intl)));
        logError(new Error('Failed to update segments after routing.'));
        return { isSuccess: false };
      }

      return { isSuccess: true, data: { ...rest, segments: updatedSegments } };
    },
    [routing, dispatch, intl]
  );

  return rerouteSegments;
};

export default useRerouteSegments;
